import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { LanguageSelectorPopup, LanguageSwitcher } from "./components/LanguageSelector";
import MaintenanceMode from "./components/MaintenanceMode";
import ErrorBoundary from "./components/ErrorBoundary";
import { LoadingFallback } from "./components/LazyComponents";

// Lazy load pages for better initial bundle size
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

// Configure QueryClient with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Set to false to disable maintenance mode
const MAINTENANCE_MODE_ENABLED = false;

const App = () => {
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    // Check if user has dev access from localStorage
    const devAccess = localStorage.getItem('dev_access');
    if (devAccess === 'true') {
      setHasAccess(true);
    }
  }, []);

  // Show maintenance page if enabled and user doesn't have access
  if (MAINTENANCE_MODE_ENABLED && !hasAccess) {
    return (
      <LanguageProvider>
        <MaintenanceMode onUnlock={() => setHasAccess(true)} />
      </LanguageProvider>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <LanguageSelectorPopup />
            <BrowserRouter>
              <Suspense fallback={<LoadingFallback message="Loading page..." />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/track-order" element={<TrackOrder />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
